import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, provide } from 'vue'
import i18n from '../../../VueI18n'
import { useRegisters } from '../../../@web/modules/@shared/composables/Registers.composable'
import { useMessages } from '../../../composables/useMessages'

import Button from '../../../components/Button/Button.vue'
import CrudForm from '../../../components/CrudForm/CrudForm.vue'
import CrudTable from '../../../components/CrudTable/CrudTable.vue'
import Dropdown from '../../../components/Dropdown/Dropdown.vue'
import GridPagination from '../../../components/GridPagination/GridPagination.vue'
import RadioButtons from '../../../components/RadioButtons/RadioButtons.vue'
import Tag from '../../../components/Tag/Tag.vue'
import TagWithButton from '../../../components/TagWithButton/TagWithButton.vue'

import CardPicker from '../../../components/CardPicker/CardPicker.vue'
import DropdownCheckbox from '../../../components/DropdownCheckbox/DropdownCheckbox.vue'
import DropdownCheckboxFk from '../../../components/DropdownCheckboxFk/DropdownCheckboxFk.vue'
import DropdownFk from '../../../components/DropdownFk/DropdownFk.vue'
import InputSearch from '../../../components/InputSearch/InputSearch.vue'
import InputText from '../../../components/InputText/InputText.vue'
import KebabMenu from '../../../components/KebabMenu/KebabMenu.vue'
import Loading from '../../../components/Loading/Loading.vue'

import ModelPreviewTemp from '../../../components/ModelPreviewTemp/ModelPreviewTemp.vue'
import ProcessamentoAvancado from '../ProcessamentoAvancado.vue'

import { getAllEstratoInventarioCubagem } from '../../../services/requests/EstratoInventarioCubagemRequests'
import { getAllProdutos } from '../../../services/requests/ProdutoRequest'
import {
	ajustaModeloHipsometrico,
	createEstratoInventario,
	deleteEstratoInventarioFlorestalByIdList,
	getAllEstratosInventarioFlorestalPagination,
	getAllHipsometricoWithGraphicByEiflId,
	processaInventario,
	saveChosenHipsometricoModel,
	updateEstratoInventarioFlorestalAsync,
	classificaComoInventarioOficial,
} from '../../../services/requests/EstratoInventarioFlorestalRequests'
import { processarQualidadeParaInventarioTradicional } from '../../../services/requests/EstratoInventarioDeQualidadeRequests'
import { getDropdownMedicaoProcessamento } from '../../../services/requests/MedicoesInventarioRequests'
import { getDropdownEstratosFlorestais } from '../../../services/requests/EstratoFlorestalRequests'
import { getInstalacoesSingleTrees } from '../../../services/requests/OrdemDeServicoRequests'

import { exportarCsv } from '../../../utils/csvHellpers'
import OfficialProcessingIcon from '../../../components/OfficialProcessingIcon/OfficialProcessingIcon.vue'
import ExclusionAlert from '../../../components/ExclusionAlert/ExclusionAlert.vue'
import DropdownCheckboxFKMedicao from '../../../components/DropdownCheckboxFkMedicao/DropdownCheckboxFKMedicao.vue'

// ── Messages composable ──

export default /*#__PURE__*/_defineComponent({
  __name: 'InventarioFlorestal',
  setup(__props) {

const { addMessage } = useMessages()

// ── Template ref ──
const paginationRef = ref<InstanceType<typeof GridPagination> | null>(null)

// ── Registers composable (replaces ControllingPaginationMixins + RegistersMixins) ──
const {
	quantityPerPage,
	setStartEnd,
	setQuantityPerPage,
	disableSaveButton,
	isEdit,
	modelList,
	orderTable,
	showForm,
	titleForm,
	textFilter,
	sortBy,
	sortType,
	isDeleteButtonDisabled,
	modelsIdWithCheckedToDelete,
	modelsToBeShowedAndTotal,
	cleanModel,
} = useRegisters({
	onTextFilterChange: () => {
		paginationRef.value?.resetToFirstPageInputHandle()
	},
})

// ── Data (refs) ──
const tipoProcessamento = ref('tradicional')
const medicoesOriginal = ref<any[]>([])
const filtrarMedicaoBruta = ref(false)
const variaveis = ref<string[]>([])
const totalRecords = ref(0)
const exportando = ref(false)
const showExclusionAlert = ref(false)
const descricao = ref('')
const modeloSalvo = ref<any>(undefined)
const escolhendoModelo = ref(false)
const processando = ref(false)
const descriptionChanged = ref(false)
const headersSelecionados = ref([
	'estrato',
	'eifl_tx_status',
	'medicao',
	'eifl_ds_descricao',
	'eifl_cd_codigo',
	'nomeModelo',
	'eifl_dt_processamento',
	'inventarioOficial',
])
const ordensServico = ref<any[]>([])
const modeloEscolhido = ref('')
const index = ref(0)
const modelos = ref<any[]>([])
const avancado = ref(true)
const nivelEstrato = ref('')
const produtosSelecionados = ref<string[]>([])
const inventarioCubagens = ref<any[]>([])
const produtos = ref<any[]>([])
const intention = ref<any>({})
const planejamentoModelo = ref(false)
const mostrarLoadingProcessando = ref(false)
const mensagemProcessando = ref(i18n.t('realizeOProcessamento') as string)

const model = ref<Record<string, any>>({
	id: '',
	eifl_ds_descricao: '',
	fk_mdco_medicao: '',
	fk_orsv_ordem_servico: '',
	fk_eicb_estrato_invcbg: '',
	eifl_is_subsequente: false,
	eifl_md_nivel_significancia: 0.05,
	eifl_md_altura_toco: 0.1,
	inventarioOficial: true,
	novoInventario: false,
})

const tableConfiguration = ref({
	statusTable: i18n.t('loading') + '...',
	prefix: 'OS',
	subtitleCardPropertie: 'eifl_ds_descricao',
	titleCardPropertie: 'eifl_ds_descricao',
})

const tableHeaders = ref([
	{ key: 'eifl_cd_codigo', value: i18n.t('code'), isActive: true },
	{ key: 'eifl_dt_processamento', value: i18n.t('processingDate'), isActive: true },
	{ key: 'eifl_ds_descricao', value: i18n.t('description'), isActive: true },
	{ key: 'estrato', value: i18n.t('forestStratification'), isActive: true },
	{ key: 'ordemDeServico', value: i18n.t('serviceOrder'), isActive: false },
	{ key: 'medicao', value: i18n.t('measurement'), isActive: true },
	{ key: 'eifl_tx_status', value: i18n.t('status'), isActive: true },
	{ key: 'nomeModelo', value: i18n.t('model'), isActive: true },
	{ key: 'inventarioOficial', value: i18n.t('registration_status'), isActive: false },
])

const estratosFlorestais = ref<any[]>([])
const executoresSelecionados = ref<any[]>([])

// ── Computed ──
const medicoesComputadas = computed(() => {
	let medicoesParaProcessar = medicoesOriginal.value
	if (filtrarMedicaoBruta.value) {
		medicoesParaProcessar = medicoesOriginal.value.filter(
			(medicao: any) => medicao.medicaoProcessada === false,
		)
	}
	return medicoesParaProcessar.map((medicao: any) => ({
		[i18n.t('measurement') as string]: `${medicao.codigoMedicao} - ${medicao.descricaoMedicao}`,
		[i18n.t('isMeasurementProcessed') as string]: medicao.medicaoProcessada
			? i18n.t('yes')
			: i18n.t('no'),
		[i18n.t('serviceOrder') as string]:
			`${medicao.codigoOrdemServico} - ${medicao.descricaoOrdemServico}`,
		[i18n.t('farm') as string]: `${medicao.codigoFazenda} - ${medicao.nomeFazenda}`,
		id: medicao.id,
	}))
})

const isSingleTrees = computed(() => tipoProcessamento.value === 'singletrees')

const agregadoras = computed<Record<string, string>>(() => ({
	tlho_cd_codigo: 'talhao',
	eiflid: 'estrato',
	ptio_dt_plantio: 'idade',
	mtge_cd_codigo: 'matgen',
	prlaid: 'parcela',
}))

const edit = computed(() => isEdit.value)

const cubagens = computed(() => {
	return inventarioCubagens.value.map((cubagem: any) => {
		return Object.assign(cubagem, {
			[i18n.t('modelName') as string]: cubagem.nomeModelo,
			[i18n.t('code') as string]: cubagem.eicb_cd_codigo,
			[i18n.t('description') as string]: cubagem.eicb_ds_descricao,
			[i18n.t('type') as string]: cubagem.eicb_tx_tipo_modelo,
			[i18n.t('logging') as string]: cubagem.cubagem,
		})
	})
})

const kebabOptions = computed(() => [
	{
		value: 'editar',
		label: i18n.t('edit'),
	},
])

const headersAtivos = computed(() => {
	const ativos = tableHeaders.value.filter((f: any) => headersSelecionados.value.includes(f.key))
	ativos.forEach((f: any) => {
		f.isActive = true
	})
	return ativos
})

const ordensServicoFormatadas = computed(() => {
	return ordensServico.value.map((ordem: any) => ({
		Código: ordem.codigo,
		Descrição: ordem.descricao,
		Bitterlich: ordem.isBitterlich ? i18n.t('yes') : i18n.t('no'),
		Mapeado: ordem.isMapped ? i18n.t('yes') : i18n.t('no'),
		id: ordem.ordemServicoId,
	}))
})

const produtosFormatados = computed(() => {
	return produtos.value.map((produto: any) => ({
		Nome: produto.tppr_ds_descricao,
		Código: produto.tppr_cd_codigo,
		[i18n.t('logLength') as string]: produto.tppr_md_comprimento_tora,
		[i18n.t('maxDiameter') as string]: produto.tppr_md_diametro_max,
		[i18n.t('minDiameter') as string]: produto.tppr_md_diametro_min,
		id: produto.id,
	}))
})

const produtosEscolhidos = computed(() => {
	return produtos.value
		.filter((produto: any) => produtosSelecionados.value.includes(produto.id))
		.map((produto: any) => ({
			fk_eifl_estrato_invflo: model.value.id,
			fk_tppr_produto: produto.id,
			inpr_ds_descricao: produto.tppr_ds_descricao,
			inpr_is_com_casca: false,
			inpr_md_comprimento_max: produto.tppr_md_comprimento_tora,
			inpr_md_comprimento_min: produto.tppr_md_comprimento_tora,
			inpr_md_diametro_max: produto.tppr_md_diametro_max,
			inpr_md_diametro_min: produto.tppr_md_diametro_min,
		}))
})

const variaveisAgregadoras = computed(() => {
	return variaveis.value.toString().replaceAll(',', ' ').trim()
})

const isProcessamentoCubagemAfilamento = computed(() => {
	if (model.value.fk_eicb_estrato_invcbg) {
		const found = inventarioCubagens.value.find(
			(inventarioCubagem: any) => inventarioCubagem.id === model.value.fk_eicb_estrato_invcbg,
		)
		return found?.eicb_tx_tipo_modelo === 'Afilamento'
	}
	return false
})

const camposObrigatoriosPreenchidos = computed(() => {
	const descricaoPreenchida = model.value.eifl_ds_descricao
	const estratoPreenchido = model.value.fk_eicb_estrato_invcbg
	const medicaoOuOrdemPreenchido = model.value.fk_mdco_medicao || model.value.fk_orsv_ordem_servico
	const variaveisAgregadorasPreenchidas = variaveisAgregadoras.value
	const descricaoEditada = isEdit.value ? model.value.eifl_ds_descricao !== descricao.value : true
	const produtoPreenchidoSeAfilamento = isProcessamentoCubagemAfilamento.value
		? produtosSelecionados.value.length > 0
		: true

	return (
		descricaoPreenchida &&
		estratoPreenchido &&
		medicaoOuOrdemPreenchido &&
		variaveisAgregadorasPreenchidas &&
		descricaoEditada &&
		produtoPreenchidoSeAfilamento
	)
})

// ── Functions ──
function toggleExclusionAlert() {
	showExclusionAlert.value = !showExclusionAlert.value
}

function handleCheckboxChange(event: any) {
	filtrarMedicaoBruta.value = event.target.checked
}

function onDescriptionChange(value: string) {
	if (isEdit.value) {
		descriptionChanged.value = descricao.value !== value
	}
}

function handleVariaveis(variavel: string) {
	if (variaveis.value.includes(variavel)) {
		variaveis.value.splice(variaveis.value.indexOf(variavel), 1)
	} else {
		variaveis.value.push(variavel)
	}

	const conjunto = ['mtge_cd_codigo', 'ptio_dt_plantio']
	variaveis.value = variaveis.value.filter((f) =>
		conjunto.includes(variavel) ? conjunto.includes(f) : f === variavel,
	)
}

function sortConfigDate(event: any) {
	if (event.sortType === 'asc') {
		modelList.value.sort((a: any, b: any) => {
			const dateA = new Date(a.eifl_dt_processamento.split('/').reverse().join('-'))
			const dateB = new Date(b.eifl_dt_processamento.split('/').reverse().join('-'))
			return dateA.getTime() - dateB.getTime()
		})
	} else if (event.sortType === 'desc') {
		modelList.value.sort((a: any, b: any) => {
			const dateA = new Date(a.eifl_dt_processamento.split('/').reverse().join('-'))
			const dateB = new Date(b.eifl_dt_processamento.split('/').reverse().join('-'))
			return dateB.getTime() - dateA.getTime()
		})
	}
}

function setarComoProcessamentoOficial(mdl: any) {
	mdl.inventarioOficial = !mdl.inventarioOficial
	const inventario = {
		EstratoInventarioFlorestalId: mdl.id,
		SingleTrees: mdl.eifl_is_singletrees,
		Oficial: mdl.inventarioOficial,
		NovoInventario: false,
	}
	if (
		inventario.EstratoInventarioFlorestalId &&
		inventario.EstratoInventarioFlorestalId.trim() !== ''
	) {
		classificaComoInventarioOficial(inventario)
			.then((response: any) => {
				try {
					if (response.data.statusCode === 200) {
						showForm.value = false
						addMessage({
							title: i18n.t('sucesso') as string,
							type: 'success',
						})
						window.location.reload()
					}
				} catch (error: any) {
					addMessage({
						title: i18n.t('aviso') as string,
						description: error.response.data.Message,
						type: 'warning',
					})
				}
			})
			.catch((error: any) => {
				addMessage({
					title: i18n.t('aviso') as string,
					description: error.response.data.Message,
					type: 'warning',
				})
				changeFormAndCloseAllActions()
			})
	}
}

function deletaProcessamento(estratoInventarioFlorestalId: string | string[]) {
	const ids = Array.isArray(estratoInventarioFlorestalId)
		? estratoInventarioFlorestalId
		: [estratoInventarioFlorestalId]
	deleteEstratoInventarioFlorestalByIdList(ids).then(() => {
		ids.forEach((id) => {
			const indexToRemove = modelList.value.map((m: any) => m.id).indexOf(id)
			if (indexToRemove !== -1) {
				modelList.value.splice(indexToRemove, 1)
			}
		})
	})
}

function verPlanejamento() {
	if (modelos.value) {
		planejamentoModelo.value = true
		showForm.value = false
	}
}

function formataEstratoInventarioParaTabela(ordemDeServico: any) {
	if (ordemDeServico.eifl_is_singletrees == true) {
		const metodologiaNumeroFustesEnum: Record<number, string> = {
			0: 'Mapeamento',
			1: 'Bitterlich',
			2: 'Roleta',
			3: 'Teórico',
			4: 'CadastroManual',
		}
		ordemDeServico.eifl_nm_metodologia_nfustes =
			metodologiaNumeroFustesEnum[ordemDeServico.eifl_nm_metodologia_nfustes] || 'Desconhecido'
	}

	ordemDeServico.estrato =
		ordemDeServico.esfl_cd_codigo && ordemDeServico.esfl_ds_descricao
			? `${ordemDeServico.esfl_cd_codigo} - ${ordemDeServico.esfl_ds_descricao}`
			: ordemDeServico.esfl_ds_descricao || ''

	ordemDeServico.isCheckedToDelete = false
}

async function registraInventarioFlorestal() {
	processando.value = true
	mostrarLoadingProcessando.value = true
	mensagemProcessando.value = 'Aguarde enquanto os modelos são gerados'
	const isSubsequente =
		produtosEscolhidos.value && produtosEscolhidos.value.length > 0
			? true
			: model.value.eifl_is_subsequente
	model.value.eifl_is_subsequente = isSubsequente

	await createEstratoInventario({
		eifl_ds_descricao: model.value.eifl_ds_descricao,
		eifl_dt_processamento: new Date(),
		eifl_dt_referencia: new Date(),
		eifl_is_subsequente: isSubsequente,
		eifl_md_altura_toco: model.value.eifl_md_altura_toco,
		eifl_tx_var_agregadoras: variaveisAgregadoras.value,
		fk_mdco_medicao: model.value.fk_mdco_medicao,
		fk_eicb_estrato_invcbg: model.value.fk_eicb_estrato_invcbg,
		eifl_cd_codigo: 0,
		eifl_md_volume_florestal: 0,
		eifl_tx_status: '',
		eifl_tx_query_gerada: '',
		InventarioProdutos: produtosEscolhidos.value,
		fk_orsv_ordem_servico: model.value.fk_orsv_ordem_servico,
		eifl_is_singletrees: isSingleTrees.value,
		eifl_md_nivel_significancia: model.value.eifl_md_nivel_significancia,
		eifl_nm_metodologia_nfustes: model.value.eifl_nm_metodologia_nfustes,
		InventarioOficial: model.value.inventarioOficial,
		NovoInventario: true,
	})
		.then((response: any) => {
			model.value.id = response.data
			addMessage({
				title: i18n.t('aviso') as string,
				description: i18n.t('ajustandoModelosHipsometricos') as string,
				type: 'info',
			})
			ajustaModeloHipsometrico({
				EiflId: model.value.id,
				MedicaoId: model.value.fk_mdco_medicao || model.value.fk_orsv_ordem_servico,
				VariavelAgregadora: variaveisAgregadoras.value.replace(' ', '-'),
				isSingleTrees: isSingleTrees.value,
			})
				.then((response: any) => {
					modelos.value = response.data.result
					processando.value = false
					mostrarLoadingProcessando.value = false
				})
				.catch(() => {
					planejamentoModelo.value = true
					processando.value = false
					mostrarLoadingProcessando.value = false
					addMessage({
						title: i18n.t('erro') as string,
						description: i18n.t('falhaAoCriarProcessamento') as string,
						type: 'error',
					})
					deletaProcessamento([model.value.id])
				})
		})
		.catch(() => {
			planejamentoModelo.value = true
			addMessage({
				title: i18n.t('erro') as string,
				description: i18n.t('falhaAoCriarProcessamento') as string,
				type: 'error',
			})
			deletaProcessamento([model.value.id])
		})
}

function saveModel() {
	escolhendoModelo.value = true
	const userModules = JSON.parse(localStorage.getItem('user-modules') || '[]')

	if (
		!userModules.includes('IA_SELECTED_MODELS') &&
		modelos.value[index.value].stan_nm_variavel_modelo === 'Ajuste11'
	) {
		escolhendoModelo.value = false
		addMessage({
			title: i18n.t('aviso') as string,
			description: i18n.t('avisoSemModuloIASelectedModel') as string,
			type: 'warning',
		})
		return
	}

	addMessage({
		title: i18n.t('aviso') as string,
		description: i18n.t('salvandoModelo') as string,
		type: 'info',
	})
	saveChosenHipsometricoModel({
		EiflId: model.value.id,
		MedicaoId: model.value.fk_mdco_medicao || model.value.fk_orsv_ordem_servico,
		ChosenModelName: modelos.value[index.value].stan_nm_variavel_modelo,
		isSingleTrees: isSingleTrees.value,
	})
		.then(() => {
			addMessage({
				title: i18n.t('aviso') as string,
				description: i18n.t('iniciandoProcessamento') as string,
				type: 'info',
			})
			processaInventario({
				EiflId: model.value.id,
				EicbId: model.value.fk_eicb_estrato_invcbg,
				DiametroComercial: null,
				AlturaToco: model.value.eifl_md_altura_toco,
				IsSubsequente: model.value.eifl_is_subsequente,
				Processamento: isProcessamentoCubagemAfilamento.value ? 'Sortimento' : 'Volumetrico',
				isSingleTrees: isSingleTrees.value,
				ordemServicoId: model.value.fk_orsv_ordem_servico || null,
				nivelSignificancia: model.value.eifl_md_nivel_significancia,
				metodologiaNFustes: model.value.eifl_nm_metodologia_nfustes,
			})
				.then(() => {
					addMessage({
						title: i18n.t('aviso') as string,
						description: 'Processando informações de qualidade para o inventário',
						type: 'info',
					})

					// Volta para a listagem e atualiza a grid sem recarregar a pagina.
					// So deve rodar DEPOIS que a qualidade terminar: um window.location.reload()
					// aqui abortaria a request de qualidade ainda em andamento.
					const finalizar = () => {
						escolhendoModelo.value = false
						planejamentoModelo.value = false
						showForm.value = false
						isEdit.value = false
						index.value = 0
						modelos.value = []
						modeloSalvo.value = undefined
						cleanModel(model)
						refreshTabela(0, 0, '')
					}

					if (!isSingleTrees.value) {
						processarQualidadeParaInventarioTradicional({
							idInventarioFlorestal: model.value.id,
							idMedicao: model.value.fk_mdco_medicao,
							nivelSignificancia: model.value.eifl_md_nivel_significancia,
						})
							.then(() => {
								escolhendoModelo.value = false
								planejamentoModelo.value = false
								showForm.value = false
								addMessage({
									title: i18n.t('sucesso') as string,
									description: i18n.t('planejamentoSalvoComSucesso') as string,
									type: 'success',
								})
								// So recarrega apos a qualidade concluir, evitando cancelar a request
								finalizar()
							})
							.catch((error: any) => {
								console.error(
									'Erro ao processar qualidade para inventario tradicional:',
									error?.response?.data ?? error?.message ?? error,
								)
								addMessage({
									title: i18n.t('erro') as string,
									description: i18n.t('erroAoSalvarProcessamento') as string,
									type: 'error',
								})
								escolhendoModelo.value = false
								// Em caso de erro nao recarrega, para o usuario ver a mensagem
							})
					} else {
						finalizar()
					}
				})
				.catch((error: any) => {
					console.log(
						'Erro ao salvar processamento:',
						error?.response?.data ?? error?.message ?? error,
					)
					addMessage({
						title: i18n.t('erro') as string,
						description: i18n.t('erroAoSalvarProcessamento') as string,
						type: 'error',
					})
					escolhendoModelo.value = false
				})
		})
		.catch((error: any) => {
			console.log('Erro ao salvar modelo:', error?.response?.data ?? error?.message ?? error)
			addMessage({
				title: i18n.t('erro') as string,
				description: i18n.t('erroAoSalvarModelo') as string,
				type: 'error',
			})
		})
}

function searchModel(text: string) {
	const modelo = modelos.value.find((m: any) =>
		m.stan_nm_titulo_modelo.toLowerCase().includes(text.toLowerCase()),
	)
	if (modelo) {
		index.value = modelos.value.indexOf(modelo)
	}
}

function handleClick(tipo: string) {
	avancado.value = tipo === 'avancado'
}

function update() {
	updateEstratoInventarioFlorestalAsync({
		id: model.value.id,
		eifl_ds_descricao: model.value.eifl_ds_descricao,
		eifl_tx_var_agregadoras: variaveisAgregadoras.value,
		eifl_tx_status: model.value.eifl_tx_status,
		eicb_cd_codigo: model.value.eicb_cd_codigo,
		eifl_is_singletrees: isSingleTrees.value,
		eifl_is_subsequente: model.value.eifl_is_subsequente,
		eifl_md_altura_toco: model.value.eifl_md_altura_toco,
		eifl_md_diametro_comercial: model.value.eifl_md_diametro_comercial,
		eifl_md_nivel_significancia: model.value.eifl_md_nivel_significancia,
		fk_eicb_estrato_invcbg: model.value.fk_eicb_estrato_invcbg,
		fk_mdco_medicao: model.value.fk_mdco_medicao,
		fk_orsv_ordem_servico: model.value.fk_orsv_ordem_servico,
		eifl_nm_metodologia_nfustes: model.value.eifl_nm_metodologia_nfustes,
	})
		.then(() => {
			addMessage({
				title: i18n.t('sucesso') as string,
				type: 'success',
			})
			showForm.value = false
			descriptionChanged.value = false
			window.location.reload()
		})
		.catch(() => {
			addMessage({
				title: i18n.t('erro') as string,
				description: i18n.t('erroAoAtualizarProcessamento') as string,
				type: 'error',
			})
		})
}

function deleteModels(email: string) {
	const emailUsuario = localStorage.getItem('email')
	if (email === emailUsuario) {
		const inventarioSelecionado = modelList.value.filter((inventarioFlorestal: any) =>
			modelsIdWithCheckedToDelete.value.includes(inventarioFlorestal.id),
		)

		const inventariosSelecionados = inventarioSelecionado.map(
			(inventarioFlorestal: any) =>
				`• ${inventarioFlorestal.eifl_cd_codigo} / ${inventarioFlorestal.eifl_ds_descricao}\n`,
		)

		const confirmResult = confirm(i18n.t('confirmDelete') + '\n ' + inventariosSelecionados)

		if (confirmResult) {
			deleteEstratoInventarioFlorestalByIdList(modelsIdWithCheckedToDelete.value).then(() => {
				modelsIdWithCheckedToDelete.value.forEach((f: string) => {
					const indexToRemove = modelList.value.map((m: any) => m.id).indexOf(f)
					modelList.value.splice(indexToRemove, 1)
				})
				addMessage({
					title: i18n.t('sucesso') as string,
					type: 'success',
				})
				showExclusionAlert.value = false
			})
		}
	} else {
		addMessage({
			title: i18n.t('wrongEmail') as string,
			description: i18n.t('wrongEmaildescricao') as string,
			type: 'error',
		})
	}
}

function saveClick() {
	if (isEdit.value) {
		update()
	} else {
		planejamentoModelo.value = true
		registraInventarioFlorestal()
		showForm.value = false
	}
}

function showEditModelForm(mdl: any) {
	produtosSelecionados.value = mdl.produtos?.map((m: any) => m.fk_tppr_produto) || []
	model.value = { ...mdl }
	descricao.value = model.value.eifl_ds_descricao
	nivelEstrato.value = agregadoras.value[model.value.eifl_tx_var_agregadoras] ?? ''
	variaveis.value = (model.value.eifl_tx_var_agregadoras ?? '').split(' ')
	titleForm.value = `${i18n.t('edit')} ${i18n.t('inventoryProcessing')}`
	isEdit.value = true
	if (mdl.eifl_is_singletrees) {
		tipoProcessamento.value = 'singletrees'
	} else {
		tipoProcessamento.value = 'tradicional'
	}
	changeFormAndCloseAllActions()

	getAllHipsometricoWithGraphicByEiflId(model.value.id).then((response: any) => {
		isEdit.value = true
		model.value = { ...mdl }
		modelos.value = response.data.result
		modeloSalvo.value = response.data.result
	})
}

function showNewModelForm() {
	cleanModel(model)
	executoresSelecionados.value = []
	variaveis.value = []
	tipoProcessamento.value = 'tradicional'
	model.value.eifl_md_altura_toco = 0.1
	model.value.eifl_md_nivel_significancia = 0.05

	titleForm.value = `${i18n.t('create')} ${i18n.t('inventoryProcessing')}`
	isEdit.value = false
	model.value.inventarioOficial = true
	changeFormAndCloseAllActions()
}

function changeFormAndCloseAllActions() {
	showForm.value = !showForm.value
	modelos.value = []
	descriptionChanged.value = false
}

/**
 * Baixa CSV com o resultado FILTRADO COMPLETO (nao so a pagina ja carregada):
 * busca tudo numa chamada antes de gerar o arquivo, sem mexer no estado da grid.
 */
async function downloadCSVProcessamentoInventarioFlorestal() {
	if (exportando.value) return
	exportando.value = true
	try {
		const response: any = await getAllEstratosInventarioFlorestalPagination(
			0,
			Math.max(totalRecords.value, 100),
			textFilter.value,
		)
		const items = response.data.result || []

		// Mesma formatacao aplicada pela grid ao carregar (refreshTabela)
		items.forEach((item: any) => {
			const dataProcessamento = new Date(item.eifl_dt_processamento)
			const dia = String(dataProcessamento.getDate()).padStart(2, '0')
			const mes = String(dataProcessamento.getMonth() + 1).padStart(2, '0')
			const ano = dataProcessamento.getFullYear()
			item.eifl_dt_processamento = `${dia}/${mes}/${ano}`
			formataEstratoInventarioParaTabela(item)
		})

		const cabecalhoDoCsvInventarioFlorestal = [
			'Código',
			'Data do Processamento',
			'Descrição',
			'Estrato Florestal',
			'Ordem de Serviço',
			'Medição',
			'Estado',
			'Modelo',
		]

		const linhas = items.map((item: any) => [
			item.eifl_cd_codigo,
			item.eifl_dt_processamento,
			item.eifl_ds_descricao,
			item.estrato,
			item.ordemDeServico,
			item.mdco_ds_descricao,
			item.eifl_tx_status,
			item.nomeModelo,
		])

		exportarCsv(
			'export_processamentos_inventario_florestal',
			cabecalhoDoCsvInventarioFlorestal,
			linhas,
		)
	} finally {
		exportando.value = false
	}
}

function refreshTabela(pageNumber?: number, pageSize?: number, filteringWord?: string) {
	getAllEstratosInventarioFlorestalPagination(
		pageNumber ?? 0,
		pageSize ?? 0,
		filteringWord ?? '',
	).then((response: any) => {
		const inventarios = response.data.result
		totalRecords.value = response.data.result[0].totalRegistros
		if (!inventarios || inventarios.length === 0) {
			return
		}
		inventarios.forEach((inventario: any) => {
			const dataProcessamento = new Date(inventario.eifl_dt_processamento)
			const dia = String(dataProcessamento.getDate()).padStart(2, '0')
			const mes = String(dataProcessamento.getMonth() + 1).padStart(2, '0')
			const ano = dataProcessamento.getFullYear()
			inventario.eifl_dt_processamento = `${dia}/${mes}/${ano}`
			formataEstratoInventarioParaTabela(inventario)
		})
		modelList.value = inventarios
		tableConfiguration.value.statusTable = i18n.t('noRecordFound') as string
	})
}

// ── Watchers ──
watch(
	tableHeaders,
	(value) => {
		headersSelecionados.value = value.filter((f: any) => f.isActive).map((m: any) => m.key)
	},
	{ deep: true },
)

watch(intention, (value) => {
	const foundModel = modelList.value.find((m: any) => m.id === value.properties?.id)
	if (!foundModel) return
	switch (value.intent) {
		case 'editar':
			showEditModelForm(foundModel)
			break
		case 'visualizar':
			getAllHipsometricoWithGraphicByEiflId(foundModel.id).then((response: any) => {
				isEdit.value = true
				model.value = foundModel
				modelos.value = response.data.result
				planejamentoModelo.value = true
			})
			break
	}
})

watch(isSingleTrees, (value) => {
	if (value) nivelEstrato.value = ''
})

watch(
	() => model.value.fk_tpsv_tipo_servico,
	() => {
		model.value.orsv_md_medida_parcela = ''
		model.value.orsv_tx_forma_parcela = ''
		model.value.orsv_md_geofence = ''
	},
)

watch(textFilter, (value) => {
	refreshTabela(0, 0, value)
})

// ── Provide ──
provide('edit', edit)

// ── Initialization (equivalent to created()) ──
quantityPerPage.value = 10
orderTable.value = ['orsv_cd_codigo']

getAllProdutos().then((response: any) => {
	produtos.value = response.data.result
})

getAllEstratoInventarioCubagem().then((response: any) => {
	inventarioCubagens.value = response.data.result
})

getAllEstratosInventarioFlorestalPagination(0, 0, '').then((response: any) => {
	const inventarios = response.data.result
	totalRecords.value = response.data.result[0].totalRegistros
	inventarios.forEach((inventario: any) => {
		const dataProcessamento = new Date(inventario.eifl_dt_processamento)
		const dia = String(dataProcessamento.getDate()).padStart(2, '0')
		const mes = String(dataProcessamento.getMonth() + 1).padStart(2, '0')
		const ano = dataProcessamento.getFullYear()
		inventario.eifl_dt_processamento = `${dia}/${mes}/${ano}`
		formataEstratoInventarioParaTabela(inventario)
	})
	modelList.value = inventarios
	tableConfiguration.value.statusTable = i18n.t('noRecordFound') as string
})

getDropdownEstratosFlorestais().then((response: any) => {
	estratosFlorestais.value = response
})

getDropdownMedicaoProcessamento().then((response: any) => {
	medicoesOriginal.value = response
})

getInstalacoesSingleTrees().then((response: any) => {
	ordensServico.value = response.data.result
})

return { __sfc: true,addMessage, paginationRef, quantityPerPage, setStartEnd, setQuantityPerPage, disableSaveButton, isEdit, modelList, orderTable, showForm, titleForm, textFilter, sortBy, sortType, isDeleteButtonDisabled, modelsIdWithCheckedToDelete, modelsToBeShowedAndTotal, cleanModel, tipoProcessamento, medicoesOriginal, filtrarMedicaoBruta, variaveis, totalRecords, exportando, showExclusionAlert, descricao, modeloSalvo, escolhendoModelo, processando, descriptionChanged, headersSelecionados, ordensServico, modeloEscolhido, index, modelos, avancado, nivelEstrato, produtosSelecionados, inventarioCubagens, produtos, intention, planejamentoModelo, mostrarLoadingProcessando, mensagemProcessando, model, tableConfiguration, tableHeaders, estratosFlorestais, executoresSelecionados, medicoesComputadas, isSingleTrees, agregadoras, edit, cubagens, kebabOptions, headersAtivos, ordensServicoFormatadas, produtosFormatados, produtosEscolhidos, variaveisAgregadoras, isProcessamentoCubagemAfilamento, camposObrigatoriosPreenchidos, toggleExclusionAlert, handleCheckboxChange, onDescriptionChange, handleVariaveis, sortConfigDate, setarComoProcessamentoOficial, deletaProcessamento, verPlanejamento, formataEstratoInventarioParaTabela, registraInventarioFlorestal, saveModel, searchModel, handleClick, update, deleteModels, saveClick, showEditModelForm, showNewModelForm, changeFormAndCloseAllActions, downloadCSVProcessamentoInventarioFlorestal, refreshTabela, Button, CrudForm, CrudTable, Dropdown, GridPagination, RadioButtons, Tag, TagWithButton, CardPicker, DropdownCheckbox, DropdownCheckboxFk, DropdownFk, InputSearch, InputText, KebabMenu, Loading, ModelPreviewTemp, ProcessamentoAvancado, OfficialProcessingIcon, ExclusionAlert, DropdownCheckboxFKMedicao }
}

})